/**
 * Pre-filled enquiry links - email and WhatsApp.
 *
 * Every contact point on the site opens with the message already written: a
 * greeting, placeholders for the sender's name and company, and an opener
 * chosen by the page they came from. The visitor sees we have done the first
 * bit of work for them, and the message that lands carries context instead of
 * arriving as a bare "hi".
 *
 * Placeholders are square-bracketed so it is obvious at a glance what to edit.
 */

import { ENQUIRY_EMAIL as EMAIL } from "./brand";

/** Digits only - `wa.me` rejects the display format (+27 82 457 8390). */
export const WHATSAPP_NUMBER = "27824578390";

export { ENQUIRY_EMAIL } from "./brand";

type Topic = {
  /** Email subject line. Unused by WhatsApp, which has no subject. */
  subject: string;
  /** One line saying why they are getting in touch. */
  opener: string;
};

const DEFAULT_TOPIC: Topic = {
  subject: "Enquiry from the Larasoft website",
  opener: "I would like to chat about getting my systems talking to each other.",
};

/**
 * Longest matching prefix wins, so `/legal/paia` can differ from `/legal`.
 * A path only matches on an exact hit or a `/` boundary, so a sibling route
 * whose name merely starts the same way (`/pricing` vs `/pricing-guide`) never
 * inherits the wrong topic.
 */
const TOPIC_BY_PREFIX: Record<string, Topic> = {
  "/pricing": {
    subject: "Pricing enquiry",
    opener: "I have a question about your pricing.",
  },
  "/proof-of-concept": {
    subject: "Free proof of concept",
    opener:
      "I would like to talk about a free proof of concept on my own data.",
  },
  "/connectors": {
    subject: "Connector enquiry",
    opener: "I would like to ask about connecting one of my systems.",
  },
  "/contact": {
    subject: "Enquiry from the Larasoft website",
    opener: "I would like to speak to someone about what Larasoft could do.",
  },
  "/status": {
    subject: "System status enquiry",
    opener: "I have a question about system status.",
  },
  "/legal/paia": {
    subject: "PAIA request",
    opener: "I would like to make a PAIA request.",
  },
  "/legal/privacy": {
    subject: "Privacy enquiry",
    opener: "I have a question about how my personal information is handled.",
  },
  "/legal/data-processing": {
    subject: "Data processing enquiry",
    opener: "I have a question about your data processing terms.",
  },
  "/legal/refund-policy": {
    subject: "Refund enquiry",
    opener: "I have a question about your refund policy.",
  },
  "/legal/terms-of-use": {
    subject: "Terms of use enquiry",
    opener: "I have a question about your terms of use.",
  },
};

/**
 * Only commercial enquiries get asked what systems they run. The question is
 * off-key on a PAIA request, a refund or a privacy query, where the person
 * wants one specific thing and has no interest in our discovery process.
 *
 * Everything outside `/legal` is commercial, which keeps this true by default
 * for pages added later.
 */
function isCommercial(pathname: string): boolean {
  return !(pathname === "/legal" || pathname.startsWith("/legal/"));
}

function topicFor(pathname: string): Topic {
  const match = Object.keys(TOPIC_BY_PREFIX)
    .filter((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
    .sort((a, b) => b.length - a.length)[0];

  return match ? TOPIC_BY_PREFIX[match] : DEFAULT_TOPIC;
}

/** The email subject for a given page. */
export function enquirySubject(pathname: string): string {
  return topicFor(pathname).subject;
}

/**
 * The pre-filled EMAIL body. Prose, because an inbox expects a message: the
 * placeholders prompt for the two things that make a reply useful (who they
 * are, what they run) without blocking anyone from deleting the lot and
 * writing their own.
 */
export function enquiryBody(pathname: string): string {
  return [
    "Hi Larasoft",
    "",
    "My name is [your name] and I am with [your company].",
    "",
    topicFor(pathname).opener,
    ...(isCommercial(pathname)
      ? ["", "The systems we run are: [for example Xero, Cin7, a POS, spreadsheets]"]
      : []),
    "",
  ].join("\n");
}

/** `mailto:` link with the subject and body already written. */
export function enquiryMailto(pathname: string): string {
  const subject = encodeURIComponent(enquirySubject(pathname));
  // RFC 6068 specifies CRLF for line breaks in a mailto body. A bare LF works
  // in most clients but collapses the body onto one line in some Windows ones.
  const body = encodeURIComponent(enquiryBody(pathname).replace(/\n/g, "\r\n"));
  return `mailto:${EMAIL}?subject=${subject}&body=${body}`;
}

/**
 * The pre-filled WHATSAPP message.
 *
 * Phrased as unfinished sentences rather than "Name:" labels (Allan,
 * 2026-08-01) - a chat is a conversation, and a form with colons reads like a
 * database screen. Each line trails off so it is obvious where to carry on
 * typing.
 *
 * The work email is asked for here but NOT in the email template, where the
 * sender's address arrives with the message anyway. WhatsApp carries only a
 * phone number, so without this line a good lead has no email route back.
 *
 * The last line arrives already completed from the page they tapped from, so an
 * untouched send still says what it is about. Every opener starts with "I",
 * which is what lets it sit after "because" - `enquiry.test.ts` pins that.
 */
export function enquiryWhatsappBody(pathname: string): string {
  return [
    "Hi Larasoft,",
    "My name is ",
    "I work at ",
    "My work email is ",
    "The systems we use are ",
    `I am getting in touch because ${topicFor(pathname).opener}`,
  ].join("\n");
}

/** `wa.me` link with the form-style message. WhatsApp has no subject line. */
export function enquiryWhatsapp(pathname: string): string {
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    enquiryWhatsappBody(pathname),
  )}`;
}
